import React, { useState, useContext, useEffect } from "react";
import { Row, Col, Card, Carousel, Modal, Button } from "react-bootstrap";
import {
  FaPhone,
  FaBed,
  FaBath,
  FaRulerCombined,
  FaMapMarkerAlt,
  FaEnvelope,
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useParams, useNavigate } from "react-router-dom";
import { InfoContext } from "../providers/InfosContextProvider";
import { buildPublicAssetPath } from "../utils/publicAssetPath";
import ProgressiveImage from "../components/ProgressiveImage";
import {
  IMAGE_SIZES,
  getPlaceholderStyle,
  useCarouselImages,
} from "../utils/images";

const Property = () => {
  const [showModal, setShowModal] = useState(false);
  const [currentProperty, setCurrentProperty] = useState(null);
  const [isCarouselPaused, setIsCarouselPaused] = useState(false);
  const [ok, setOk] = useState(false);
  const { houses, texts } = useContext(InfoContext);
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (houses.length !== 0) {
      const foundProperty = houses.find((c) => c.id === parseInt(id));
      if (!foundProperty) {
        navigate("/", { replace: true });
      } else {
        setOk(true);
      }
    }
  }, [houses, id, navigate]);

  const property = houses.find((c) => c.id === parseInt(id));
  const imageCount = property?.images.length ?? 0;
  const mainCarousel = useCarouselImages(imageCount);
  const modalCarousel = useCarouselImages(
    (currentProperty ?? property)?.images.length ?? 0,
  );

  useEffect(() => {
    const titleBase =
      property?.location || texts?.Property?.description || "Imóvel";
    document.title = `${titleBase} | Casas do Nabão`;
  }, [property, texts]);

  const handleOpenModal = (index) => {
    modalCarousel.onSelect(index);
    setCurrentProperty(property);
    setIsCarouselPaused(true);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setIsCarouselPaused(false);
  };

  const handleMsg = (propertyId) => {
    navigate(`/contact?id=${propertyId}`);
  };

  return (
    <>
      {ok && property && (
        <>
          <Header />
          <div className="property-details-container">
            <Row>
              <Col xs={12} md={6} className="fixed-col">
                <Card className="property-details-card">
                  <Carousel
                    interval={isCarouselPaused ? null : 3000}
                    activeIndex={mainCarousel.activeIndex}
                    onSelect={mainCarousel.onSelect}
                  >
                    {property.images.map((image, index) => (
                      <Carousel.Item key={image}>
                        <div
                          className="carousel-image-container"
                          style={getPlaceholderStyle(image)}
                          onClick={() => handleOpenModal(index)}
                        >
                          {mainCarousel.shouldRender(index) && (
                            <ProgressiveImage
                              className="d-block w-100"
                              src={image}
                              sizes={IMAGE_SIZES.detail}
                              alt={`Slide ${index}`}
                              priority={index === 0}
                              onLoad={mainCarousel.markReady}
                              {...(index !== mainCarousel.activeIndex && {
                                loading: "eager",
                                fetchPriority: "low",
                              })}
                            />
                          )}
                        </div>
                      </Carousel.Item>
                    ))}
                  </Carousel>
                  <Card.Body>
                    <Card.Title>
                      <div className="property-details-efficiency">
                        {property.price}
                        {property.efficiency.map((efficiency, index) => (
                          <img
                            key={index}
                            src={buildPublicAssetPath(
                              `/assets/images/efficiency/${efficiency}_RGB.png`,
                            )}
                            alt={`Eficiência Energética ${efficiency}`}
                          />
                        ))}
                      </div>
                    </Card.Title>
                    <Row>
                      <Col className="text-center">
                        <span>
                          <FaMapMarkerAlt /> {property.location}
                        </span>
                      </Col>
                    </Row>
                    <Row className="justify-content-center align-items-end mt-2 mb-2">
                      {property.area && (
                        <Col xs="auto">
                          <span className="text-dark">
                            <FaRulerCombined /> {property.area}
                          </span>
                        </Col>
                      )}
                      {property.bedrooms && (
                        <Col xs="auto">
                          <span className="text-dark">
                            <FaBed /> {property.bedrooms}
                          </span>
                        </Col>
                      )}
                      {property.bathrooms && (
                        <Col xs="auto">
                          <span className="text-dark">
                            <FaBath /> {property.bathrooms}
                          </span>
                        </Col>
                      )}
                    </Row>
                    <div className="property-contacts">
                      <Row>
                        <Col xs={12}>
                          <div className="property-contact-row d-flex align-items-center justify-content-between flex-wrap mt-3">
                            <p className="mb-0">
                              <FaPhone className="me-2" />
                              <a
                                href={`tel:${property.contacts.phone
                                  .replace(/\+/g, "00")
                                  .replace(/\s/g, "")}`}
                              >
                                {property.contacts.phone}
                              </a>
                            </p>
                            <Button
                              className="btn-sell"
                              onClick={() => handleMsg(property.id)}
                            >
                              <FaEnvelope className="me-2" />
                              {`${texts.Property.sendMessage}`}
                            </Button>
                          </div>
                        </Col>
                      </Row>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
              <Col xs={12} md={6} className="fixed-col">
                <div className="property-description">
                  <h3>{`${texts.Property.description}`}</h3>
                  <div className="property-description-text">
                    {property.description
                      .split(/\.\s+/)
                      .map((para, index, array) => (
                        <p key={index}>
                          {para}
                          {index < array.length - 1 ? "." : ""}
                        </p>
                      ))}
                  </div>

                  <div className="property-ref-ami-container mt-4 d-flex justify-content-end align-items-center">
                    <span className="ami-text me-3">AMI 23473</span>
                    <span className="ref-tag">
                      {`${texts.Property.ref}`} {property.id}
                    </span>
                  </div>
                </div>
              </Col>
            </Row>
          </div>

          <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
            <Carousel
              activeIndex={modalCarousel.activeIndex}
              onSelect={modalCarousel.onSelect}
            >
              {(currentProperty ?? property).images.map((image, index) => (
                <Carousel.Item key={image}>
                  <div
                    className="carousel-image-container"
                    onClick={() => handleOpenModal(index)}
                  >
                    {modalCarousel.shouldRender(index) && (
                      <ProgressiveImage
                        className="d-block w-100 modal-image"
                        src={image}
                        sizes={IMAGE_SIZES.fullscreen}
                        alt={`Slide ${index}`}
                        loading="eager"
                        onLoad={modalCarousel.markReady}
                      />
                    )}
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </Modal>

          <Footer />
        </>
      )}
    </>
  );
};

export default Property;
